import React, { useState } from 'react';
import './AdminPage.css';
import portfolioData from '../data/portfolio-data.json';

// Google Sheets URLs (same as in App.js)
const HERO_CAROUSEL_SHEET_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQZTLeP4sJrYbvA7H3xxARywowmdJKhw82XxogolYsoarxkfOeQfyJCag_xkHMtHR2wCOIalvtKXg1m/pub?gid=219441820&single=true&output=csv';
const CAROUSEL_SHEET_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQZTLeP4sJrYbvA7H3xxARywowmdJKhw82XxogolYsoarxkfOeQfyJCag_xkHMtHR2wCOIalvtKXg1m/pub?gid=2067140409&single=true&output=csv';
const ASSETS_SHEET_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQZTLeP4sJrYbvA7H3xxARywowmdJKhw82XxogolYsoarxkfOeQfyJCag_xkHMtHR2wCOIalvtKXg1m/pub?gid=1280232217&single=true&output=csv';
const GALLERIES_SHEET_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQZTLeP4sJrYbvA7H3xxARywowmdJKhw82XxogolYsoarxkfOeQfyJCag_xkHMtHR2wCOIalvtKXg1m/pub?gid=1082273483&single=true&output=csv';
const NEWS_SHEET_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQZTLeP4sJrYbvA7H3xxARywowmdJKhw82XxogolYsoarxkfOeQfyJCag_xkHMtHR2wCOIalvtKXg1m/pub?gid=1580558307&single=true&output=csv';

// CSV Parser - Handles quoted fields with commas AND newlines properly
const parseCSV = (csvText) => {
  const rows = [];
  let currentRow = [];
  let currentField = '';
  let inQuotes = false;

  // Parse character by character to handle multi-line quoted fields
  for (let i = 0; i < csvText.length; i++) {
    const char = csvText[i];
    const nextChar = csvText[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote ("") - add one quote and skip next
        currentField += '"';
        i++;
      } else {
        // Toggle quote mode
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of field
      currentRow.push(currentField.trim());
      currentField = '';
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      // End of row (handle both \n and \r\n)
      if (char === '\r' && nextChar === '\n') {
        i++; // Skip the \n in \r\n
      }
      // Only add row if it has content
      if (currentField || currentRow.length > 0) {
        currentRow.push(currentField.trim());
        if (currentRow.some(field => field !== '')) {
          rows.push(currentRow);
        }
        currentRow = [];
        currentField = '';
      }
    } else {
      // Regular character (including newlines inside quotes)
      currentField += char;
    }
  }

  // Handle last field and row
  if (currentField || currentRow.length > 0) {
    currentRow.push(currentField.trim());
    if (currentRow.some(field => field !== '')) {
      rows.push(currentRow);
    }
  }

  if (rows.length === 0) return [];

  // First row is headers
  const headers = rows[0];
  const data = [];

  // Convert remaining rows to objects
  for (let i = 1; i < rows.length; i++) {
    const row = {};
    headers.forEach((header, index) => {
      row[header] = rows[i][index] || '';
    });
    data.push(row);
  }

  return data;
};

// Fetch functions
const fetchHeroCarouselData = async () => {
  try {
    const response = await fetch(HERO_CAROUSEL_SHEET_URL);
    if (!response.ok) return [];
    const csvText = await response.text();
    if (csvText.includes('<html>') || csvText.includes('<!DOCTYPE')) return [];
    return parseCSV(csvText);
  } catch (error) {
    console.error('Error fetching hero carousel data:', error);
    return [];
  }
};

const fetchCarouselData = async () => {
  try {
    const response = await fetch(CAROUSEL_SHEET_URL);
    if (!response.ok) return [];
    const csvText = await response.text();
    if (csvText.includes('<html>') || csvText.includes('<!DOCTYPE')) return [];
    return parseCSV(csvText);
  } catch (error) {
    console.error('Error fetching carousel data:', error);
    return [];
  }
};

const fetchAssetsData = async () => {
  try {
    const response = await fetch(ASSETS_SHEET_URL);
    if (!response.ok) return [];
    const csvText = await response.text();
    if (csvText.includes('<html>') || csvText.includes('<!DOCTYPE')) return [];
    return parseCSV(csvText);
  } catch (error) {
    console.error('Error fetching assets data:', error);
    return [];
  }
};

const fetchGalleriesData = async () => {
  try {
    const response = await fetch(GALLERIES_SHEET_URL);
    if (!response.ok) return [];
    const csvText = await response.text();
    if (csvText.includes('<html>') || csvText.includes('<!DOCTYPE')) return [];
    return parseCSV(csvText);
  } catch (error) {
    console.error('Error fetching galleries data:', error);
    return [];
  }
};

const fetchNewsData = async (assetsData = []) => {
  try {
    const response = await fetch(NEWS_SHEET_URL);
    if (!response.ok) return [];
    const csvText = await response.text();
    if (csvText.includes('<html>') || csvText.includes('<!DOCTYPE')) return [];
    const parsed = parseCSV(csvText);

    // Filter only live news items and map fields (natural row order)
    const liveNews = parsed
      .filter(item => item['Live?'] === 'TRUE' || item['live?'] === 'TRUE' || item['Live'] === 'TRUE')
      .map(item => {
        const artworkId = item['Art Page'] || item['art page'] || '';
        const url = item['Full Link'] || item['full link'] || '';
        let finalLink = '';

        // Validation: Only one can be filled at a time
        if (artworkId && url) {
          // Both filled = no link
          finalLink = '';
        } else if (artworkId) {
          // Generate link from artwork ID by finding its slug
          const artwork = assetsData.find(asset => asset['artwork-id'] === artworkId);
          if (artwork && artwork.slug) {
            // Convert underscores to hyphens to match routing logic
            const normalizedSlug = artwork.slug.replace(/_/g, '-');
            finalLink = `/art/${normalizedSlug}`;
          }
        } else if (url) {
          // Use the provided URL
          finalLink = url;
        }

        return {
          'Category': item['Category'] || item['category'] || '',
          'Date': item['Date'] || item['date'] || '',
          'Headline': item['Headline'] || item['headline'] || '',
          'Content': item['Content'] || item['content'] || '',
          'Link': finalLink,
          'thumbnail': item['thumbnail'] || item['Thumbnail'] || ''
        };
      });

    return liveNews;
  } catch (error) {
    console.error('Error fetching news data:', error);
    return [];
  }
};

const AdminPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('');

  // Check if in development mode
  const isDevelopment = process.env.NODE_ENV === 'development';

  // Download Data (DD) - Sync from Google Sheets
  const handleDownloadData = async () => {
    setIsLoading(true);
    setStatus('Syncing data from Google Sheets...');

    try {
      console.log('Syncing portfolio data...');

      const [heroCarouselResult, carouselResult, assetsResult, galleriesResult] = await Promise.allSettled([
        fetchHeroCarouselData(),
        fetchCarouselData(),
        fetchAssetsData(),
        fetchGalleriesData()
      ]);

      let heroCarouselData = [];
      let carouselData = [];
      let assetsData = [];
      let heroIds = [];
      let galleriesData = [];
      let newsData = [];

      // Process hero carousel data
      if (heroCarouselResult.status === 'fulfilled') {
        const rawHeroCarousel = heroCarouselResult.value;

        // Sort by Order column and extract Artwork IDs
        heroIds = rawHeroCarousel
          .sort((a, b) => {
            const orderA = parseInt(a['Order'] || a['order']) || 999;
            const orderB = parseInt(b['Order'] || b['order']) || 999;
            return orderA - orderB;
          })
          .map(row => row['Artwork ID'] || row['artwork-id'] || row['Artwork id'] || '')
          .filter(id => id !== '');

        console.log('Hero carousel synced:', heroIds.length, 'items');
        setStatus(`Hero carousel synced: ${heroIds.length} items`);
      } else {
        console.error('Failed to sync hero carousel:', heroCarouselResult.reason);
        setStatus('Error: Failed to sync hero carousel data');
        setIsLoading(false);
        return;
      }

      // Process carousel data
      if (carouselResult.status === 'fulfilled') {
        const rawCarousel = carouselResult.value;

        carouselData = rawCarousel.map(row => ({
          'project-id': row['Project ID'] || row['project-id'] || '',
          'name': row['Name'] || row['name'] || '',
          'name-asset': row['Name Asset'] || row['name-asset'] || '',
          'hello-1st': row['Hello 1st'] || row['hello-1st'] || '',
          'hello-2nd': row['Hello 2nd'] || row['hello-2nd'] || '',
          'filter-method': row['Filter Method'] || row['filter-method'] || '',
          'live': row['live'] || row['Live'] || ''
        }));

        console.log('Carousel data synced:', carouselData.length, 'items');
        setStatus(`Carousel data synced: ${carouselData.length} items`);
      } else {
        console.error('Failed to sync carousel:', carouselResult.reason);
        setStatus('Error: Failed to sync carousel data');
        setIsLoading(false);
        return;
      }

      // Process assets data
      if (assetsResult.status === 'fulfilled') {
        const rawAssets = assetsResult.value;

        assetsData = rawAssets
          .filter(row => row['Live?'] === 'TRUE')
          .map(row => {
            const type = row['Type'] || 'Drawing';
            const projectId = type.toLowerCase() + 's';

            // Normalize slug: convert underscores to hyphens for consistent routing
            const rawSlug = row['Slug'] || '';
            const normalizedSlug = rawSlug.replace(/_/g, '-');

            return {
              'project-id': projectId,
              'artwork-id': row['Artwork ID'] || '',
              'filename': row['Image'] || '',
              'type': type,
              'title': row['Title'] || '',
              'description': row['Description'] || '',
              'alt-text': row['Alt Text'] || row['Alt text'] || '',
              'background-size': '',
              'size': row['Size'] || '110%',
              'fit': row['Fit'] || 'contain',
              'orientation': row['Orientation'] || 'vertical',
              'slug': normalizedSlug,
              'status': row['Status'] || '',
              'day': row['Day'] || '',
              'month': row['Month'] || '',
              'year': row['Year'] || '',
              'medium': row['Medium'] || '',
              'surface': row['Surface'] || '',
              'dimensions': row['Dimensions'] || '',
              'thumbnail': row['Thumb'] || '',
              'hero-background': row['Hero background'] || '',
              'hero-background-tiny': row['Hero background tiny'] || '',
              'hero-assets': row['Hero assets'] || 'none',
              'extra-images': row['Extra Images'] || row['extra images'] || '0',
              'og-image': row['OG Image'] || row['og image'] || row['OG image'] || '',
              'YouTube': row['YouTube'] || '',
              'Instagram': row['Instagram'] || '',
              'TikTok': row['TikTok'] || '',
              'sub-headline': row['Sub headline'] || ''
            };
          });

        console.log('Assets data synced:', assetsData.length, 'assets');
        setStatus(prev => prev + ` | Assets: ${assetsData.length}`);
      } else {
        console.warn('Assets sheet not available:', assetsResult.reason);
        assetsData = portfolioData.assets || [];
      }

      // Process galleries data
      if (galleriesResult.status === 'fulfilled') {
        const rawGalleries = galleriesResult.value;

        galleriesData = rawGalleries.map(row => ({
          'id': row['ID'] || row['id'] || '',
          'filter-prefix': row['Filter Prefix'] || row['filter-prefix'] || '',
          'title': row['Title'] || row['title'] || '',
          'title-image': row['Title Image'] || row['title-image'] || '',
          'description': row['Description'] || row['description'] || '',
          'display-type': row['Display Type'] || row['display-type'] || '',
          'slug': row['Slug'] || row['slug'] || '',
          'banner-asset': row['Banner Asset'] || row['banner-asset'] || '',
          'live': row['live'] || row['Live'] || ''
        }));

        console.log('Galleries data synced:', galleriesData.length, 'galleries');
      } else {
        console.warn('Galleries sheet not available:', galleriesResult.reason);
        galleriesData = portfolioData.galleries || [];
      }

      // Process news data (must happen after assets are processed)
      try {
        newsData = await fetchNewsData(assetsData);
        console.log('News data synced:', newsData.length, 'items');
      } catch (error) {
        console.warn('News sheet not available:', error);
        newsData = portfolioData.news || [];
      }

      // Create JSON data object
      const jsonData = {
        hero: heroIds,
        projects: carouselData,
        assets: assetsData,
        galleries: galleriesData,
        news: newsData
      };

      // Download JSON file
      const jsonString = JSON.stringify(jsonData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = 'portfolio-data.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setStatus('✅ Success! JSON file downloaded. Replace src/data/portfolio-data.json with the downloaded file.');
      console.log('Portfolio sync completed! JSON file downloaded.');

    } catch (error) {
      console.error('Sync failed:', error);
      setStatus('❌ Error: Sync failed. Check console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  // Clear Storage (CS) - Reset localStorage and reload
  const handleClearStorage = () => {
    if (window.confirm('Are you sure you want to clear all states? This will reset carousel positions, autoplay, and mute settings, then reload the page.')) {
      setStatus('Clearing localStorage...');

      localStorage.removeItem('portfolio-carousel-positions');
      localStorage.removeItem('portfolio-autoplay-state');
      localStorage.removeItem('portfolio-mute-state');

      setStatus('✅ Storage cleared! Reloading page...');

      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  };

  // If not in development, show access denied
  if (!isDevelopment) {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <h1 className="admin-title">Access Denied</h1>
          <p className="admin-description">
            This page is only accessible in development mode.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-container">
        <h1 className="admin-title">DYAR Arts Admin Panel</h1>
        <p className="admin-description">
          Development-only admin tools for managing portfolio data
        </p>

        <div className="admin-buttons">
          <button
            className="admin-button download-button"
            onClick={handleDownloadData}
            disabled={isLoading}
          >
            <span className="button-icon">📥</span>
            <span className="button-text">Download Data</span>
            <span className="button-subtitle">Sync from Google Sheets</span>
          </button>

          <button
            className="admin-button clear-button"
            onClick={handleClearStorage}
            disabled={isLoading}
          >
            <span className="button-icon">🗑️</span>
            <span className="button-text">Clear Storage</span>
            <span className="button-subtitle">Reset all localStorage</span>
          </button>
        </div>

        {status && (
          <div className="admin-status">
            <p>{status}</p>
          </div>
        )}

        <div className="admin-info">
          <h2>Instructions</h2>
          <ul>
            <li><strong>Download Data (DD):</strong> Fetches latest data from Google Sheets and downloads a JSON file. Replace <code>src/data/portfolio-data.json</code> with the downloaded file.</li>
            <li><strong>Clear Storage (CS):</strong> Clears all localStorage (carousel positions, autoplay state, mute state) and reloads the page.</li>
          </ul>
          <p className="admin-warning">
            ⚠️ This page only works in development mode (<code>npm start</code>). It will show "Access Denied" in production builds.
          </p>
        </div>

        <div className="admin-info">
          <h2>Pre-Launch Checklist</h2>
          <ul>
            <li>✅ <strong>Admin page:</strong> Auto-disabled in production (no action needed)</li>
            <li>✅ <strong>Inktober 2024 gallery:</strong> Already disabled (see REACTIVATE_INKTOBER_2024.md)</li>
            <li>⚠️ <strong>Test production build:</strong> Run <code>npm run build</code> and verify /admin shows "Access Denied"</li>
            <li>⚠️ <strong>Replace favicon:</strong> Ensure /public/favicon.ico and favicon.svg are final versions</li>
            <li>⚠️ <strong>Update copyright year:</strong> Check homepage and ArtworkPage.js for current year</li>
          </ul>
        </div>

        <div className="admin-info">
          <h2>Documentation</h2>
          <ul>
            <li><strong>CLAUDE.md</strong> - Project overview and development commands</li>
            <li><strong>TECHNICAL_DOCUMENTATION.md</strong> - Comprehensive technical reference (1200+ lines)</li>
            <li><strong>AI_AGENT_BRIEF.md</strong> - Condensed tech sheet for AI agents</li>
            <li><strong>SEO_IMPLEMENTATION_GUIDE.md</strong> - SEO features and implementation notes</li>
            <li><strong>REACTIVATE_INKTOBER_2024.md</strong> - How to re-enable Inktober gallery</li>
            <li><strong>PROJECT_STATUS.md</strong> - Setup and status notes</li>
            <li><strong>IMPLEMENTATION_NOTES.md</strong> - 3-images-per-slide system</li>
            <li><strong>AUTO_GROUPING_GUIDE.md</strong> - Automatic grouping logic</li>
            <li><strong>ARTWORK_ROUTING_IMPLEMENTATION.md</strong> - Routing system details</li>
          </ul>
        </div>

        <div className="admin-info">
          <h2>Future To-Do List</h2>
          <ul>
            <li>🔲 <strong>404 Page:</strong> Custom not-found page for broken links</li>
            <li>🔲 <strong>Contact Form:</strong> Email or contact integration</li>
            <li>🔲 <strong>Open Graph Tags:</strong> Social media preview cards (Facebook, LinkedIn)</li>
            <li>🔲 <strong>Twitter Card Tags:</strong> Twitter-specific preview cards</li>
            <li>🔲 <strong>Apple Touch Icon:</strong> 180×180 PNG for iOS home screen</li>
            <li>🔲 <strong>PWA Icons:</strong> icon-192.png & icon-512.png for Android</li>
            <li>🔲 <strong>Analytics:</strong> Google Analytics or privacy-focused alternative</li>
            <li>🔲 <strong>Sitemap.xml:</strong> For better SEO indexing</li>
            <li>🔲 <strong>Loading States:</strong> Skeleton screens or spinners for slow connections</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
